// artifacts: артефакты из разрушенного на клиенте — модели, свечение, дуга
// появления, искры, звук, всплывающая подсказка «+N серебра». Что, где и
// кому — решает сервер (server/internal/game/artifacts.go); здесь только
// показ. Гостю серебро реликвии начисляется в браузере по событию подбора
// (сервер начисляет только аккаунтам).
//
// main.ts вызывает: onEvent (до своей обработки — запись pickup'а ещё
// жива), frame (кадр), appendLights (свет), reset (новая сессия).

import type { MeshInstance, PointLight, Renderer } from "../engine/renderer";
import type { LoadedMesh } from "../engine/meshLoader";
import type { ParticleSimulation } from "../engine/particles/simulate";
import type { EmitterConfig } from "../engine/particles/types";
import { playOneShot } from "../engine/sfx";
import { EvType, PickupArtifact, PickupType, type GameEvent, type PickupInit } from "../net/protocol";
import { weaponDef, weaponFromPickupType } from "../weapons/catalog";
import { GLTF_TO_WORLD, m4, m4Mul, m4Trs } from "../weapons/mat";
import { creditGuestCoins } from "../characters/progress";
import { ARTIFACT_MODELS, artifactDef, isRelicType, silverText, type ArtifactDef } from "./catalog";
import { ArtifactToasts } from "./toast";
import { t } from "../i18n";

export interface ArtifactsHost {
  renderer: Renderer;
  particles: ParticleSimulation;
  myId(): number;
  /** Гость (серебро копится в браузере). */
  guest(): boolean;
  soundVolume(): number;
  lite(): boolean;
  /** Глаз слушателя/камеры. */
  listener(): { x: number; y: number; z: number };
  pickups(): Map<number, PickupInit>;
}

/** Дуга появления, мс (сервер даёт взять через 400 мс). */
const FLIGHT_MS = 450;
/** Срок жизни на сервере — 40 с; последние 6 с предмет мигает. */
const LIFE_MS = 40000;
const BLINK_MS = 6000;
const SPIN_RPS = 0.35;
const BOB_AMP = 0.05;
const MAX_GLOW_LIGHTS = 2;
const HEAR_FULL = 4;
const HEAR_MAX = 36;
const BASE = "/models/artifacts/";

function moteP(): EmitterConfig {
  return {
    visual: "fire", blend: "additive", rate: 0,
    lifetimeMin: 0.9, lifetimeMax: 1.8,
    speedMin: 0.08, speedMax: 0.3,
    coneAngleRad: 0.5, emitDir: [0, 0, 1],
    gravityZ: 0.25, drag: 1.2, turbulence: 0.5,
    baseSize: 0.12,
    sizeOverLife: [{ t: 0, v: 0.2 }, { t: 0.3, v: 1 }, { t: 1, v: 0.05 }],
    alphaOverLife: [{ t: 0, v: 0 }, { t: 0.2, v: 0.85 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function glintP(): EmitterConfig {
  return {
    visual: "spark", blend: "additive", rate: 0,
    lifetimeMin: 0.5, lifetimeMax: 1.1,
    speedMin: 0.1, speedMax: 0.45,
    coneAngleRad: 0.6, emitDir: [0, 0, 1],
    gravityZ: -0.1, drag: 0.8, turbulence: 0.9,
    baseSize: 0.045,
    sizeOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0.2 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function burstP(): EmitterConfig {
  return {
    visual: "spark", blend: "additive", rate: 0,
    lifetimeMin: 0.35, lifetimeMax: 0.9,
    speedMin: 2.0, speedMax: 5.5,
    coneAngleRad: Math.PI, emitDir: [0, 0, 1],
    gravityZ: -6, drag: 1.6, turbulence: 0.6,
    baseSize: 0.06,
    sizeOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0.15 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function flareP(): EmitterConfig {
  return {
    visual: "fire", blend: "additive", rate: 0,
    lifetimeMin: 0.25, lifetimeMax: 0.5,
    speedMin: 0.6, speedMax: 2.2,
    coneAngleRad: Math.PI, emitDir: [0, 0, 1],
    gravityZ: 0.5, drag: 2.5, turbulence: 1.0,
    baseSize: 0.38,
    sizeOverLife: [{ t: 0, v: 0.6 }, { t: 0.3, v: 1.3 }, { t: 1, v: 0.2 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function riseP(): EmitterConfig {
  return {
    visual: "spark", blend: "additive", rate: 0,
    lifetimeMin: 0.5, lifetimeMax: 1.0,
    speedMin: 1.2, speedMax: 3.2,
    coneAngleRad: 0.35, emitDir: [0, 0, 1],
    gravityZ: -1.5, drag: 1.2, turbulence: 0.8,
    baseSize: 0.055,
    sizeOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0.1 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

interface Art {
  id: number;
  type: number;
  def: ArtifactDef;
  /** Номинал (серебро реликвии / патроны) из записи появления. */
  amount: number;
  bornMs: number;
  /** Пришёл событием появления (виден срок и дуга), а не списком новичку. */
  fresh: boolean;
  fromX: number; fromY: number; fromZ: number;
  toX: number; toY: number; toZ: number;
  x: number; y: number; z: number;
  inst: MeshInstance | null;
  phase: number;
  carry: number;
  landed: boolean;
}

interface Flash {
  x: number; y: number; z: number;
  color: [number, number, number];
  range: number;
  born: number;
  life: number;
}

export class ArtifactsClient {
  private arts = new Map<number, Art>();
  private meshes = new Map<string, LoadedMesh>();
  private flashes: Flash[] = [];
  private toasts = new ArtifactToasts();
  private moteId: number;
  private glintId: number;
  private burstId: number;
  private flareId: number;
  private riseId: number;
  private tmpA = m4();
  private timeSec = 0;
  private near: { x: number; y: number; z: number; c: [number, number, number]; d: number }[] = [];

  constructor(private host: ArtifactsHost) {
    const p = host.particles;
    this.moteId = p.registerEmitter(moteP(), { x: 0, y: 0, z: 0 });
    this.glintId = p.registerEmitter(glintP(), { x: 0, y: 0, z: 0 });
    this.burstId = p.registerEmitter(burstP(), { x: 0, y: 0, z: 0 });
    this.flareId = p.registerEmitter(flareP(), { x: 0, y: 0, z: 0 });
    this.riseId = p.registerEmitter(riseP(), { x: 0, y: 0, z: 0 });
    for (const k of ARTIFACT_MODELS) {
      void host.renderer.loadMeshFromUrl(BASE + k + ".glb")
        .then((m) => { this.meshes.set(k, m); })
        .catch((e) => console.warn(`[artifacts] ${k}.glb:`, e));
    }
    if (new URLSearchParams(location.search).get("adebug") === "1") {
      (window as unknown as Record<string, unknown>).__artifacts = {
        client: this,
        list: () => [...this.arts.values()].map((a) => ({ id: a.id, type: a.type, x: +a.x.toFixed(2), y: +a.y.toFixed(2), z: +a.z.toFixed(2), landed: a.landed })),
        /** Только показ (сервер о нём не знает): артефакт вылетает из точки
         *  над полом в dist м перед глазом и ложится; floorZ — пол. */
        fake: (type: number, x: number, y: number, floorZ: number, amount = 20) => {
          const id = 64000 + Math.floor(Math.random() * 1000);
          const p: PickupInit = {
            id, type, x, y, z: floorZ + 0.35, yaw: 0, amount, state: 0,
            artifact: PickupArtifact.Drop, fromX: x + 0.4, fromY: y - 0.3, fromZ: floorZ + 1.6,
          };
          this.onEvent({ type: EvType.PickupSpawn, pickup: p }, host.myId());
          host.pickups().set(id, p);
          return id;
        },
        /** Показ подбора своим игроком (подсказка, искры, звук). */
        take: (id: number, amount?: number) => {
          const a = this.arts.get(id);
          if (!a) return false;
          this.onEvent({ type: EvType.Pickup, pickupId: id, playerId: host.myId(), pickupType: a.type, amount: amount ?? a.amount }, host.myId());
          host.pickups().delete(id);
          return true;
        },
      };
    }
  }

  /** Событие сети (вызывать ДО обработки в main: pickup ещё в списке). */
  onEvent(ev: GameEvent, myId: number): void {
    if (ev.type === EvType.PickupSpawn) {
      if (ev.pickup.artifact === PickupArtifact.Drop) this.add(ev.pickup, true);
    } else if (ev.type === EvType.Pickup) {
      const a = this.arts.get(ev.pickupId);
      if (!a) return;
      this.onTaken(a, ev.playerId, ev.pickupType, ev.amount, myId);
      this.drop(a);
    } else if (ev.type === EvType.PickupRemove) {
      const a = this.arts.get(ev.pickupId);
      if (!a) return;
      // Не взят — истёк: тает с искрами.
      this.host.particles.emitBurst(this.glintId, a.x, a.y, a.z, this.host.renderer.getTimeAccSec(), 10, [0, 0, 1], 0.3);
      this.drop(a);
    }
  }

  /** Владеет ли модуль отрисовкой этого pickup'а (общий код его пропускает). */
  owns(p: PickupInit): boolean {
    return p.artifact === PickupArtifact.Drop && weaponFromPickupType(p.type) < 0;
  }

  /** Кадр: дуга, парение, свечение, ореол, мигание перед исчезновением. */
  frame(dt: number): void {
    this.timeSec += dt;
    const now = performance.now();
    const t = this.host.renderer.getTimeAccSec();
    const lite = this.host.lite();
    const eye = this.host.listener();
    const pickups = this.host.pickups();
    this.near.length = 0;
    // Список новичку (MsgPickupInit) — без дуги.
    for (const p of pickups.values()) {
      if (p.artifact === PickupArtifact.Drop && p.state === 0 && !this.arts.has(p.id)) this.add(p, false);
    }
    for (const a of [...this.arts.values()]) {
      const p = pickups.get(a.id);
      if (!p || p.state !== 0) {
        this.drop(a);
        continue;
      }
      const age = now - a.bornMs;
      const weapon = a.def.model === "";
      // Позиция: дуга от места разрушения, потом парение над полом.
      const k = Math.min(1, age / FLIGHT_MS);
      if (k < 1) {
        const apex = Math.max(a.fromZ, a.toZ) + 0.7;
        const u = 1 - k;
        a.x = a.fromX + (a.toX - a.fromX) * k;
        a.y = a.fromY + (a.toY - a.fromY) * k;
        a.z = u * u * a.fromZ + 2 * u * k * apex + k * k * a.toZ;
      } else {
        a.x = a.toX; a.y = a.toY; a.z = a.toZ;
        if (!a.landed) {
          a.landed = true;
          if (a.fresh) {
            this.host.particles.emitBurst(this.burstId, a.x, a.y, a.z - 0.3, t, lite ? 6 : 14, [0, 0, 1], 0.2);
            this.flash(a.x, a.y, a.z, a.def.glow, 4, 220);
          }
        }
      }
      // Оружие рисует WeaponPickups по записи pickup'а — во время дуги
      // подставляем в неё текущую точку.
      if (weapon) {
        p.x = a.x; p.y = a.y; p.z = a.z;
      }
      // Мигание в последние секунды (срок известен у пришедших событием).
      let visible = true;
      if (a.fresh && age > LIFE_MS - BLINK_MS) {
        const left = LIFE_MS - age;
        const hz = left < 2000 ? 10 : 5;
        visible = Math.floor(age / 1000 * hz) % 2 === 0;
      }
      const pulse = 0.5 + 0.5 * Math.sin(this.timeSec * 3.6 + a.phase);
      if (!weapon) this.drawModel(a, k < 1, pulse, visible);
      // Ореол: тёплые сгустки и искры вокруг (у реликвий гуще).
      if (k >= 1 && visible) {
        a.carry += dt * (lite ? 8 : (a.def.relic ? 30 : 16));
        while (a.carry >= 1) {
          a.carry -= 1;
          const ang = Math.random() * Math.PI * 2;
          const r = 0.22 + Math.random() * 0.18;
          if (Math.random() < 0.5) {
            this.host.particles.emitBurst(this.moteId, a.x + Math.cos(ang) * r, a.y + Math.sin(ang) * r, a.z - 0.3, t, 1, [0, 0, 1], 0.02);
          } else {
            this.host.particles.emitBurst(this.glintId, a.x + Math.cos(ang) * r * 0.7, a.y + Math.sin(ang) * r * 0.7, a.z - 0.1 + Math.random() * 0.4, t, 1, [0, 0, 1], 0.04);
          }
        }
      } else if (k < 1) {
        // След дуги.
        a.carry += dt * 60;
        while (a.carry >= 1) {
          a.carry -= 1;
          this.host.particles.emitBurst(this.glintId, a.x, a.y, a.z, t, 1, [0, 0, 1], 0.08);
        }
      }
      const d = Math.hypot(a.x - eye.x, a.y - eye.y, a.z - eye.z);
      if (visible) {
        const g = 0.65 + 0.35 * pulse;
        this.near.push({ x: a.x, y: a.y, z: a.z + 0.3, c: [a.def.glow[0] * g, a.def.glow[1] * g, a.def.glow[2] * g], d });
      }
    }
    this.near.sort((x, y) => x.d - y.d);
    const nowMs = now;
    this.flashes = this.flashes.filter((f) => nowMs - f.born < f.life);
  }

  private drawModel(a: Art, flying: boolean, pulse: number, visible: boolean): void {
    const mesh = this.meshes.get(a.def.model);
    if (!mesh) return;
    if (!a.inst || a.inst.mesh !== mesh) {
      if (a.inst) this.host.renderer.removeMeshInstance(a.inst);
      const inst = this.host.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
      inst.worldMatrix = m4();
      this.host.renderer.addMeshInstance(inst);
      a.inst = inst;
    }
    const inst = a.inst;
    inst.hidden = !visible;
    const spin = flying ? 3.0 : SPIN_RPS;
    const yaw = this.timeSec * spin * Math.PI * 2 + a.phase;
    const bob = flying ? 0 : Math.sin(this.timeSec * 2.1 + a.phase) * BOB_AMP;
    const sc = a.def.scale;
    // Низ модели — на ~0,15 м над полом (точка парения a.z = пол + 0,35).
    const z = a.z + (flying ? 0 : a.def.lift * sc - 0.2) + bob;
    m4Trs(this.tmpA, a.x, a.y, z, 0, 0, yaw, sc);
    m4Mul(inst.worldMatrix!, this.tmpA, GLTF_TO_WORLD);
    inst.worldAabbDirty = true;
    const g = a.def.glow;
    const kk = (a.def.relic ? 0.85 : 0.35) + (a.def.relic ? 1.1 : 0.45) * pulse;
    inst.emissive = [g[0] * kk, g[1] * kk, g[2] * kk];
  }

  private add(p: PickupInit, fresh: boolean): void {
    const def = artifactDef(p.type);
    if (!def) return;
    const old = this.arts.get(p.id);
    if (old) this.drop(old);
    const a: Art = {
      id: p.id, type: p.type, def, amount: p.amount,
      bornMs: fresh ? performance.now() : performance.now() - FLIGHT_MS,
      fresh,
      fromX: fresh ? p.fromX : p.x, fromY: fresh ? p.fromY : p.y, fromZ: fresh ? p.fromZ : p.z,
      toX: p.x, toY: p.y, toZ: p.z,
      x: p.x, y: p.y, z: p.z,
      inst: null,
      phase: (p.id * 1.37) % (Math.PI * 2),
      carry: 0,
      landed: !fresh,
    };
    this.arts.set(p.id, a);
    if (!fresh) return;
    // Появление: вспышка цветом артефакта, искры во все стороны, звук.
    const t = this.host.renderer.getTimeAccSec();
    const lite = this.host.lite();
    this.host.particles.emitBurst(this.burstId, p.fromX, p.fromY, p.fromZ, t, lite ? 14 : 34, [0, 0, 1], 0.25);
    this.host.particles.emitBurst(this.flareId, p.fromX, p.fromY, p.fromZ, t, lite ? 3 : 7, [0, 0, 1], 0.2);
    this.flash(p.fromX, p.fromY, p.fromZ, def.glow, 7, 420);
    this.playAt("/sounds/artifacts/spawn.wav", p.fromX, p.fromY, p.fromZ, def.relic ? 0.75 : 0.6);
  }

  private onTaken(a: Art, playerId: number, type: number, amount: number, myId: number): void {
    const tSec = this.host.renderer.getTimeAccSec();
    const lite = this.host.lite();
    this.host.particles.emitBurst(this.riseId, a.x, a.y, a.z - 0.2, tSec, lite ? 10 : 26, [0, 0, 1], 0.35);
    this.host.particles.emitBurst(this.flareId, a.x, a.y, a.z, tSec, lite ? 2 : 5, [0, 0, 1], 0.15);
    this.flash(a.x, a.y, a.z + 0.3, a.def.glow, 5, 360);
    const relic = isRelicType(type);
    if (playerId !== myId) {
      this.playAt(relic ? "/sounds/artifacts/relic.wav" : "/sounds/artifacts/pickup.wav", a.x, a.y, a.z, 0.4);
      return;
    }
    this.play2d(relic ? "/sounds/artifacts/relic.wav" : "/sounds/artifacts/pickup.wav", relic ? 0.8 : 0.55);
    if (relic) {
      // Гостю — в браузер (сервер начисляет только аккаунтам).
      if (amount > 0 && this.host.guest()) creditGuestCoins(amount);
      const capped = amount < a.amount;
      this.toasts.show({
        icon: `${BASE}icons/${a.def.model}.png`,
        main: silverText(amount),
        sub: capped ? `${t(a.def.title)} · ${t("предел реликвий за матч")}` : t(a.def.title),
        color: a.def.glow, relic: true,
      });
      return;
    }
    if (type === PickupType.Ammo) {
      this.toasts.show({ icon: `${BASE}icons/art_ammo.png`, main: t("+{n} патронов", { n: amount }), sub: t(a.def.title), color: a.def.glow, relic: false });
    } else if (type === PickupType.Medkit) {
      this.toasts.show({ icon: `${BASE}icons/art_medkit.png`, main: t("Здоровье восполнено"), sub: t(a.def.title), color: a.def.glow, relic: false });
    } else {
      const w = weaponFromPickupType(type);
      if (w >= 0) {
        const wd = weaponDef(w);
        this.toasts.show({
          icon: `/models/weapons/icons/${wd.key}.png`, main: t(wd.title),
          sub: amount > 0 ? `${t("находка в обломках")} · ${t("+{n} патронов", { n: amount })}` : t("находка в обломках"),
          color: a.def.glow, relic: false,
        });
      }
    }
  }

  private drop(a: Art): void {
    if (a.inst) this.host.renderer.removeMeshInstance(a.inst);
    a.inst = null;
    this.arts.delete(a.id);
  }

  private flash(x: number, y: number, z: number, c: [number, number, number], range: number, life: number): void {
    this.flashes.push({ x, y, z, color: [c[0] * 3.2, c[1] * 3.2, c[2] * 3.2], range, born: performance.now(), life });
  }

  private distVol(x: number, y: number, z: number, base: number): number {
    const l = this.host.listener();
    const d = Math.hypot(x - l.x, y - l.y, (z - l.z) * 0.6);
    if (d >= HEAR_MAX) return 0;
    const k = d <= HEAR_FULL ? 1 : 1 - (d - HEAR_FULL) / (HEAR_MAX - HEAR_FULL);
    return base * k * k * this.host.soundVolume();
  }

  private playAt(url: string, x: number, y: number, z: number, base: number): void {
    const v = this.distVol(x, y, z, base);
    if (v > 0.01) playOneShot(url, v, 0.97 + Math.random() * 0.06);
  }

  private play2d(url: string, base: number): void {
    const v = base * this.host.soundVolume();
    if (v > 0.01) playOneShot(url, v, 1);
  }

  /** Свет: ореолы ближайших артефактов и вспышки появления/подбора. */
  appendLights(out: PointLight[]): void {
    const now = performance.now();
    for (const f of this.flashes) {
      const k = 1 - (now - f.born) / f.life;
      if (k <= 0) continue;
      const s = k * k;
      out.push({ x: f.x, y: f.y, z: f.z, range: f.range * (0.6 + 0.4 * k), color: [f.color[0] * s, f.color[1] * s, f.color[2] * s], noBulb: true });
    }
    const n = Math.min(MAX_GLOW_LIGHTS, this.near.length);
    for (let i = 0; i < n; i++) {
      const q = this.near[i]!;
      out.push({ x: q.x, y: q.y, z: q.z, range: 5.5, color: [q.c[0] * 3.0, q.c[1] * 3.0, q.c[2] * 3.0], noBulb: true });
    }
  }

  /** Новая сессия: всё убрать. */
  reset(): void {
    for (const a of this.arts.values()) if (a.inst) this.host.renderer.removeMeshInstance(a.inst);
    this.arts.clear();
    this.flashes.length = 0;
    this.near.length = 0;
    this.toasts.clear();
  }
}
